import { useQuery } from '@tanstack/react-query';
import { Maximize2, Minimize2, Pause, VideoOff, X } from 'lucide-react';
import { useEffect, useRef, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { listStreams, streamsKey, type Device } from '../inventory/api';
import type { Grid } from './layouts';
import { DND_DEVICE, DND_TILE, gridCols as cols, pickStream } from './tiles';
import { LivePlayer } from './LivePlayer';

interface MosaicProps {
  grid: Grid;
  slots: (string | null)[];
  devices: Map<string, Device>;
  selected: number;
  maximized: number | null;
  /** False while the page is in the background: players are released. */
  active: boolean;
  /** Profile chosen by the operator for single views, per device. */
  overrides: Record<string, string>;
  onSelect: (position: number) => void;
  onToggleMaximize: (position: number) => void;
  onRemove: (position: number) => void;
  onAssign: (position: number, deviceId: string) => void;
  onSwap: (from: number, to: number) => void;
  /** A saved mosaic only watched (the mosaic window): no drag, drop or removal. */
  readOnly?: boolean;
}

/**
 * Grid of live tiles with roving focus: arrows move the selection, Enter
 * maximizes, Delete empties the tile, Escape restores the grid.
 */
export function Mosaic(props: MosaicProps) {
  const { grid, slots, selected, maximized, onSelect, onToggleMaximize, onRemove, readOnly } =
    props;
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const focusPending = useRef(false);
  const side = Math.sqrt(grid);

  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    refs.current[selected]?.focus();
  }, [selected, maximized]);

  const move = (to: number) => {
    focusPending.current = true;
    onSelect(to);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Keys typed on the tile buttons keep their own meaning, except Escape.
    if (e.key !== 'Escape' && !(e.target as HTMLElement).dataset.tile) return;
    const row = Math.floor(selected / side);
    const col = selected % side;
    switch (e.key) {
      case 'ArrowRight':
        if (maximized === null && col < side - 1) move(selected + 1);
        break;
      case 'ArrowLeft':
        if (maximized === null && col > 0) move(selected - 1);
        break;
      case 'ArrowDown':
        if (maximized === null && row < side - 1) move(selected + side);
        break;
      case 'ArrowUp':
        if (maximized === null && row > 0) move(selected - side);
        break;
      case 'Enter':
        focusPending.current = true;
        onToggleMaximize(selected);
        break;
      case 'Escape':
        if (maximized === null) return;
        focusPending.current = true;
        onToggleMaximize(maximized);
        break;
      case 'Delete':
      case 'Backspace':
        if (readOnly) return;
        if (slots[selected]) onRemove(selected);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const positions = maximized === null ? Array.from({ length: grid }, (_, i) => i) : [maximized];

  return (
    <div
      role="group"
      aria-label={`Mosaico ${String(side)}×${String(side)}. Use as setas para navegar${
        readOnly ? ' e Enter para maximizar.' : ', Enter para maximizar e Delete para esvaziar.'
      }`}
      onKeyDown={onKeyDown}
      className={`grid size-full gap-px bg-line ${maximized === null ? cols[grid] : cols[1]}`}
    >
      {positions.map((position) => (
        <Tile
          key={position}
          ref={(el) => {
            refs.current[position] = el;
          }}
          position={position}
          {...props}
          onToggleMaximize={(p) => {
            focusPending.current = true;
            onToggleMaximize(p);
          }}
        />
      ))}
    </div>
  );
}

interface TileProps extends MosaicProps {
  position: number;
  ref: (el: HTMLDivElement | null) => void;
}

function Tile({
  ref,
  position,
  grid,
  slots,
  devices,
  selected,
  maximized,
  active,
  overrides,
  onSelect,
  onToggleMaximize,
  onRemove,
  onAssign,
  onSwap,
  readOnly = false,
}: TileProps) {
  const deviceId = slots[position] ?? null;
  const device = deviceId ? devices.get(deviceId) : undefined;
  const isSelected = position === selected;
  const isMax = maximized === position;
  const single = grid === 1 || isMax;
  const name = device?.name ?? (deviceId ? 'Câmera indisponível' : '');
  const label = `Posição ${String(position + 1)}${name ? `: ${name}` : ', vazia'}`;

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    if (readOnly) return;
    const tile = e.dataTransfer.getData(DND_TILE);
    const dev = e.dataTransfer.getData(DND_DEVICE);
    if (tile !== '') onSwap(Number(tile), position);
    else if (dev) onAssign(position, dev);
    onSelect(position);
  };

  return (
    <div
      ref={ref}
      data-tile="true"
      tabIndex={isSelected ? 0 : -1}
      aria-label={label}
      aria-current={isSelected ? 'true' : undefined}
      draggable={!readOnly && Boolean(deviceId) && maximized === null}
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_TILE, String(position));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={onDrop}
      onClick={() => {
        onSelect(position);
      }}
      onDoubleClick={() => {
        if (deviceId) onToggleMaximize(position);
      }}
      className="group relative min-h-0 min-w-0 bg-canvas outline-none"
    >
      {deviceId ? (
        <TileBody
          deviceId={deviceId}
          device={device}
          active={active}
          preferLight={!single}
          override={single ? overrides[deviceId] : undefined}
        />
      ) : (
        <div className="grid size-full place-items-center px-3 text-center">
          <div>
            <p className="text-2xs font-medium text-fg-subtle">Posição {position + 1}</p>
            {isSelected && !readOnly && (
              <p className="mt-1 text-2xs text-fg-subtle">
                Escolha uma câmera na lista ou arraste-a para cá
              </p>
            )}
          </div>
        </div>
      )}
      {deviceId && (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-linear-to-t from-black/80 to-transparent px-2 pt-4 pb-1.5">
          <span className="min-w-0 flex-1 truncate text-2xs font-medium text-white">{name}</span>
          <span
            className={`flex items-center gap-1 transition-opacity ${
              isSelected
                ? 'opacity-100'
                : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'
            }`}
          >
            {grid > 1 && (
              <TileButton
                label={isMax ? `Restaurar mosaico` : `Maximizar ${name}`}
                onClick={() => {
                  onToggleMaximize(position);
                }}
              >
                {isMax ? (
                  <Minimize2 aria-hidden="true" className="size-3.5" />
                ) : (
                  <Maximize2 aria-hidden="true" className="size-3.5" />
                )}
              </TileButton>
            )}
            {!readOnly && (
              <TileButton
                label={`Remover ${name} da posição ${String(position + 1)}`}
                onClick={() => {
                  onRemove(position);
                }}
              >
                <X aria-hidden="true" className="size-3.5" />
              </TileButton>
            )}
          </span>
        </div>
      )}
      {/* Drawn above the video so the selection is always visible. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 ${
          isSelected
            ? 'ring-2 ring-accent ring-inset'
            : 'group-focus-visible:ring-2 group-focus-visible:ring-accent/60 group-focus-visible:ring-inset'
        }`}
      />
    </div>
  );
}

function TileButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
      className="grid size-6 place-items-center rounded text-white/80 hover:bg-white/15 hover:text-white"
    >
      {children}
    </button>
  );
}

function Notice({ icon: Icon, text }: { icon: typeof VideoOff; text: string }) {
  return (
    <div className="grid size-full place-items-center px-3 text-center">
      <div className="flex flex-col items-center gap-1.5">
        <Icon aria-hidden="true" className="size-4 text-fg-subtle" strokeWidth={1.75} />
        <p className="text-2xs text-fg-muted">{text}</p>
      </div>
    </div>
  );
}

function TileBody({
  deviceId,
  device,
  active,
  preferLight,
  override,
}: {
  deviceId: string;
  device: Device | undefined;
  active: boolean;
  preferLight: boolean;
  override: string | undefined;
}) {
  const { request } = useAuth();
  const streams = useQuery({
    queryKey: streamsKey(deviceId),
    queryFn: () => listStreams(request, deviceId),
    enabled: Boolean(device),
  });

  if (!device) return <Notice icon={VideoOff} text="Câmera indisponível ou removida" />;
  if (!device.enabled) return <Notice icon={VideoOff} text="Dispositivo desativado" />;
  if (!active) return <Notice icon={Pause} text="Pausado enquanto a aba está em segundo plano" />;
  if (streams.isPending) return <div className="size-full animate-pulse bg-surface-raised" />;
  if (streams.isError) return <Notice icon={VideoOff} text="Não foi possível carregar os perfis" />;
  const stream = pickStream(streams.data, preferLight, override);
  if (!stream)
    return <Notice icon={VideoOff} text="Nenhum stream conhecido: execute um teste de conexão" />;
  return (
    <LivePlayer
      deviceId={device.id}
      profileToken={stream.profile_token}
      label={device.name}
      variant="tile"
    />
  );
}
